import { BadgeCheck } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const PORTRAIT = "/thom-kriner.jpg";

const TOOLKIT = [
  "Lean Six Sigma",
  "WalkMe Builder",
  "MBTI & FIRO",
  "Instructional Design",
  "Project Management",
  "Business Analysis",
];

const ERAS = [
  {
    num: "01",
    label: "Digital Transformation Beginnings",
    text: "Thom's career began at the forefront of digital transformation, guiding creative professionals through the complex transition from analog tools to emerging digital media, website development, and multimedia communications. Recognizing early on that new technology fails without human alignment, he successfully integrated creative teams with their clients' business systems, workflows, and emerging IT standards.",
  },
  {
    num: "02",
    label: "Global Operations — Kinko's",
    text: "As a Branch Operations Manager at Kinko's, Thom spearheaded the implementation of critical digital workflows. Leveraging Six Sigma methodologies, he helped standardize global branch operations into a unified corporate ecosystem — establishing the operational consistency that paved the way for the company's eventual acquisition by FedEx.",
  },
  {
    num: "03",
    label: "Regulated Enterprise — Health Insurance",
    text: "In the highly regulated health insurance sector, Thom established a Business Analyst Center of Excellence to optimize operations following a massive financial systems migration. He helped launch a corporate process engineering team dedicated to eliminating waste — driving efficiencies in everything from quote-to-payment cycles to Medicare Advantage compliance with strict CMS guidelines — and ultimately stood up and structured the organization's Project Management Office talent development Center of Excellence.",
  },
  {
    num: "04",
    label: "Executive Scope — Empowered People",
    text: "As his executive scope expanded to centralizing capital investment project management and business analysis teams, Thom maintained a sharp focus on resource development and strategic allocation. True to his philosophy that optimized processes require empowered people, he implemented advanced performance feedback and measurement tools — giving his talent the precise data to identify growth areas and step into leadership roles.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-thom-section"
      className="relative border-t border-[#E2E8F0] bg-white py-24 lg:py-32"
    >
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-start gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="relative mx-auto max-w-md">
                <div
                  className="absolute -inset-6 rounded-3xl"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 20%, rgba(235,94,40,0.1) 0%, rgba(235,94,40,0) 70%)",
                  }}
                />
                <div
                  data-testid="thom-portrait-image"
                  className="relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-2 shadow-xl"
                >
                  <img
                    src={PORTRAIT}
                    alt="Portrait of Thom Kriner"
                    className="aspect-[5/6] w-full rounded-xl object-cover object-top transition-all duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-4 bottom-4 rounded-xl border border-[#E2E8F0]/60 bg-white/90 px-4 py-3 backdrop-blur">
                    <p className="font-display text-2xl text-[#0F141A]">Thom Kriner</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#EB5E28]">
                      Founder &amp; Principal Consultant
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7" data-testid="thom-bio-content">
          <SectionHeader
            index="06"
            label="About the Founder"
            testid="about-heading"
            title={
              <>
                Process architect by trade.{" "}
                <span className="italic text-[#EB5E28]">Performance strategist</span> at heart.
              </>
            }
          />
          <Reveal delay={0.14}>
            <p className="mt-8 text-base leading-relaxed text-[#4A5361] sm:text-lg">
              Thom Kriner is an organizational performance engineer who has spent his career at the
              exact intersection where business processes and human dynamics meet. With a
              multifaceted background spanning operational standardization, enterprise IT
              integration, and talent development, he founded Thom Kriner LLC to help organizations
              eliminate systemic friction and align their teams for measurable success.
            </p>
          </Reveal>

          {ERAS.map((era, i) => (
            <Reveal key={era.num} delay={0.08 + 0.06 * i}>
              <div className="mt-10 border-t border-[#F1F5F9] pt-8" data-testid={`bio-era-${era.num}`}>
                <p className="mb-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#008B9B]">
                  <span className="text-[#EB5E28]">{era.num}</span>
                  <span className="h-px w-6 bg-[#E2E8F0]" />
                  {era.label}
                </p>
                <p className="text-base leading-relaxed text-[#4A5361] sm:text-lg">{era.text}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.3}>
            <div className="mt-10 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#EB5E28]">
                The Mission
              </p>
              <p className="text-base leading-relaxed text-[#4A5361] sm:text-lg">
                As the founder of Thom Kriner LLC, Thom's mission is simple:{" "}
                <span className="font-semibold text-[#0F141A]">
                  bridge the gap between artificial intelligence and human behavior
                </span>{" "}
                so organizations can scale efficiently without leaving their people behind.
                Combining his deep expertise in Lean Six Sigma, Project Management, and Business
                Analysis with his credentials in WalkMe Builder, Instructional Design, and advanced
                behavioral diagnostics, he ensures your business systems are highly efficient — and
                your people are fully equipped to execute them.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.36}>
            <div className="mt-9 flex flex-wrap gap-3">
              {TOOLKIT.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-xs text-[#4A5361] shadow-sm"
                >
                  <BadgeCheck className="h-3.5 w-3.5 text-[#EB5E28]" />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
